/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useState, useCallback } from 'react';

/**
 * Generic hook for managing inline edit state for entities
 * Tracks which entity is being edited and the edited data
 */
export function useEntityEdit<T extends { id: string }>() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<Partial<T>>({});

  /**
   * Start editing an entity
   * Copies the entity data to editedData state
   */
  const startEdit = useCallback((entity: T) => {
    setEditingId(entity.id);
    setEditedData({ ...entity });
  }, []);

  /**
   * Cancel editing and clear state
   */
  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditedData({});
  }, []);

  /**
   * Update a single field in the edited data
   */
  const updateField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Update multiple fields at once
   */
  const updateFields = useCallback((updates: Partial<T>) => {
    setEditedData(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Check if a specific entity is being edited
   */
  const isEditing = useCallback((id: string) => editingId === id, [editingId]);

  /**
   * Clear all edit state (useful after save)
   */
  const clearEdit = useCallback(() => {
    setEditingId(null);
    setEditedData({});
  }, []);

  return {
    editingId,
    editedData,
    isEditing,
    startEdit,
    cancelEdit,
    updateField,
    updateFields,
    setEditedData,
    clearEdit
  };
}
